import { useEffect, useRef } from "react";

import { format, getTime, parseISO, set } from "date-fns";
import { useMutation, useQuery, useQueryCache } from "react-query";

import { app } from "api";
import { randomizeFileName } from "helpers";

async function getLabAppointmentsWithUsers(key: string, props: any) {
  const uid = app.auth().currentUser?.uid;

  const appointments = await app
    .firestore()
    .collectionGroup("labAppointments")
    .where("labId", "==", uid)
    .where("status", "in", ["sample_collected", "active"])
    .orderBy("scheduledCollectionTime", "asc")
    .get()
    .then((appointments) =>
      appointments.docs
        .map((i) => i.data())
        .filter(
          (i) =>
            i.status === "sample_collected" ||
            (i.status === "active" && i.submitSampleAtLab)
        )
    );

  const userIds = Array.from(new Set(appointments.map((i) => i.userId)));
  const promises = userIds.map((userId: string) =>
    app
      .firestore()
      .collection("users")
      .doc(userId)
      .get()
      .then((res) => res.data())
  );
  const users = await Promise.all(promises);
  return appointments.map((appointment) => {
    appointment["user"] = users.find((user) => user?.id === appointment.userId);
    return appointment;
  });
}

async function getLabAppointmentWithUser(id: string) {
  const appointment = (
    await app
      .firestore()
      .collectionGroup("labAppointments")
      .where("id", "==", id)
      .get()
  ).docs[0]?.data();

  if (appointment) {
    let user = (
      await app.firestore().collection("users").doc(appointment.userId).get()
    ).data();

    // if permanent registered user not found, check in temporaryUsers
    if (!user) {
      user = (
        await app
          .firestore()
          .collection("temporaryUsers")
          .doc(appointment.userId)
          .get()
      ).data();
    }

    appointment.user = user;
  }

  return appointment;
}

export function useAllAppointment({
  start,
  end,
  status,
}: {
  start?: any;
  end?: any;
  status?: string | string[];
}) {
  const uid = app.auth().currentUser?.uid;

  const unsubscribeRef = useRef<Function | null>(null);
  const queryCache = useQueryCache();

  start = start && format(new Date(start), "yyyy-MM-dd");
  end = end && format(new Date(end), "yyyy-MM-dd");

  const queryKey = ["appointments", { start, end, status }];

  let query = app
    .firestore()
    .collectionGroup("labAppointments")
    .where("labId", "==", uid);

  if (start)
    query = query.where(
      "scheduledCollectionTime",
      ">=",
      getTime(set(parseISO(start), { hours: 0, minutes: 0, seconds: 0 }))
    );
  if (end)
    query = query.where(
      "scheduledCollectionTime",
      "<",
      getTime(set(parseISO(end), { hours: 23, minutes: 59, seconds: 59 }))
    );

  if (status) {
    if (typeof status === "string") {
      query = query.where("status", "==", status);
    } else if (Array.isArray(status)) {
      query = query.where("status", "in", status);
    }
  }

  query = query.orderBy("scheduledCollectionTime", "asc");

  const queryReact = useQuery(queryKey, async () => {
    let res: firebase.default.firestore.DocumentData[] = [];

    let appointments =
      (await query.get()).docs
        .map((app) => app.data())
        .filter((i) => i.status !== "active" || i.submitSampleAtLab) ?? [];

    const userIds = Array.from(new Set(appointments.map((i) => i.userId)));
    const promises = userIds.map((userId: string) =>
      app
        .firestore()
        .collection("users")
        .doc(userId)
        .get()
        .then((res) => res.data())
    );
    const users = await Promise.all(promises);
    appointments = appointments.map((appointment) => {
      appointment["user"] = users.find(
        (user) => user?.id === appointment.userId
      );
      return appointment;
    });
    queryCache.setQueryData(queryKey, appointments);
    res = appointments;
    return res;
  });
  return queryReact;
}

export function useAllAppointmentListener({
  start,
  end,
  status,
}: {
  start?: any;
  end?: any;
  status?: string | string[];
}) {
  const uid = app.auth().currentUser?.uid;

  const unsubscribeRef = useRef<Function | null>(null);
  const queryCache = useQueryCache();

  start = start && format(new Date(start), "yyyy-MM-dd");
  end = end && format(new Date(end), "yyyy-MM-dd");

  const queryKey = ["appointments", { start, end, status }];

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  let query = app
    .firestore()
    .collectionGroup("labAppointments")
    .where("labId", "==", uid);

  if (start)
    query = query.where(
      "scheduledCollectionTime",
      ">=",
      getTime(set(parseISO(start), { hours: 0, minutes: 0, seconds: 0 }))
    );
  if (end)
    query = query.where(
      "scheduledCollectionTime",
      "<",
      getTime(set(parseISO(end), { hours: 23, minutes: 59, seconds: 59 }))
    );

  if (status) {
    if (typeof status === "string") {
      query = query.where("status", "==", status);
    } else if (Array.isArray(status)) {
      query = query.where("status", "in", status);
    }
  }

  query = query.orderBy("scheduledCollectionTime", "asc");

  const queryReact = useQuery(
    queryKey,
    async () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      let res: firebase.default.firestore.DocumentData[] = [];
      let dataFetched = false;

      const unsubscribe = await query.onSnapshot(
        async (appointmentsSnapshot) => {
          let appointments =
            appointmentsSnapshot.docs
              .map((i) => i.data())
              .filter((i) => i.status !== "active" || i.submitSampleAtLab) ??
            [];

          const userIds = Array.from(
            new Set(appointments.map((i) => i.userId))
          );
          const promises = userIds.map((userId: string) =>
            app
              .firestore()
              .collection("users")
              .doc(userId)
              .get()
              .then((res) => res.data())
          );
          const tempUserPromises = userIds.map((userId: string) =>
            app
              .firestore()
              .collection("temporaryUsers")
              .doc(userId)
              .get()
              .then((res) => res.data())
          );
          const permanetUsers = await Promise.all(promises);
          const tempUsers = await Promise.all(tempUserPromises);
          const users = [...permanetUsers, ...tempUsers]
          appointments = appointments.map((appointment) => {
            appointment["user"] = users.find(
              (user) => user?.id === appointment.userId
            );
            return appointment;
          });
          queryCache.setQueryData(queryKey, appointments);
          res = appointments;
          dataFetched = true;
        }
      );
      unsubscribeRef.current = unsubscribe;

      if (dataFetched) {
        return res;
      }
    },
    {
      cacheTime: 0,
      staleTime: Infinity,
    }
  );
  return queryReact;
}

export async function getAllLabAppointmentWithUser(
  key: string,
  {
    start,
    end,
    status,
  }: { start?: string; end?: string; status?: string | string[] }
) {
  try {
    const uid = app.auth().currentUser?.uid;

    let query = app
      .firestore()
      .collectionGroup("labAppointments")
      .where("labId", "==", uid);

    if (start)
      query = query.where(
        "scheduledCollectionTime",
        ">=",
        getTime(set(parseISO(start), { hours: 0, minutes: 0, seconds: 0 }))
      );
    if (end)
      query = query.where(
        "scheduledCollectionTime",
        "<",
        getTime(set(parseISO(end), { hours: 23, minutes: 59, seconds: 59 }))
      );

    if (status) {
      if (typeof status === "string") {
        query = query.where("status", "==", status);
      } else if (Array.isArray(status)) {
        query = query.where("status", "in", status);
      }
    }

    query = query.orderBy("scheduledCollectionTime", "asc");

    const appointments = await query
      .get()
      .then((appointments) =>
        appointments.docs
          .map((i) => i.data())
          .filter((i) => i.status !== "active" || i.submitSampleAtLab)
      );
    const userIds = Array.from(new Set(appointments.map((i) => i.userId)));
    const promises = userIds.map((userId: string) =>
      app
        .firestore()
        .collection("users")
        .doc(userId)
        .get()
        .then((res) => res.data())
    );
    const users = await Promise.all(promises);
    return appointments.map((appointment) => {
      appointment["user"] = users.find(
        (user) => user?.id === appointment.userId
      );
      return appointment;
    });
  } catch (e) {
    console.error(e);
  }
}

export function useAppointments() {
  const queryCache = useQueryCache();

  return useQuery(["appointments"], getLabAppointmentsWithUsers, {
    onSuccess: (data) => {
      data.forEach((appointment) => {
        queryCache.setQueryData(["appointments", appointment.id], appointment);
      });
    },
  });
}

export function useAppointment(id: string) {
  const queryCache = useQueryCache();
  return useQuery(["appointments", id], () => getLabAppointmentWithUser(id), {
    initialData: () => {
      const appointments = queryCache.getQueryData<any>("appointments");
      if (appointments) {
        return appointments.find((i: any) => i.id === id);
      }
    },
    initialStale: true,
  });
}

export function useUpdatePatient() {
  const queryCache = useQueryCache();

  return useMutation((data) => updatePatient(data), {
    onSuccess: (appointment: any) => {
      queryCache.setQueryData(["appointments", appointment.id], appointment);
      queryCache.invalidateQueries("appointments");
    },
    onError: (e) => {
      alert(e);
    },
  });
}

async function updatePatient(data: any) {
  if (Array.from(data.document).find((i: any) => i.size > 5 * 1024 * 1024)) {
    throw new Error("File is too big. Please upload upto 5MB only.");
  }

  const filesUploadPromises = Array.from(data.document).map((doc: any) => {
    return app
      .storage()
      .ref()
      .child("labAppointment")
      .child(data.appointment.id)
      .child(randomizeFileName(doc.name))
      .put(doc)
      .then((snap) =>
        snap.ref
          .getDownloadURL()
          .then((url) => ({ name: doc.name, url: url, createdAt: Date.now() }))
      );
  });

  const files = await Promise.all(filesUploadPromises);
  const patientProfileId = data.patient.profileId || data.patient.userId
  let updatedPatient = {
    ...data.appointment.patients[patientProfileId],
  };

  updatedPatient.status = data.status;

  updatedPatient.remarks = data.remarks || null;

  updatedPatient.files = [...(updatedPatient.files || []), ...files];

  data.appointment.patients[patientProfileId] = updatedPatient;

  let userRef = app
    .firestore()
    .collection("users")
    .doc(data.appointment.userId)

  const userExists = (await userRef.get()).exists

  if (!userExists) {
    await app
      .firestore()
      .collection("temporaryUsers")
      .doc(data.appointment.userId)
      .collection("labAppointments")
      .doc(data.appointment.id)
      .update({
        test: true,
        patients: { ...data.appointment.patients },
      });

    return syncAppointment(data.appointment);
  }

  await app
    .firestore()
    .collection("users")
    .doc(data.appointment.userId)
    .collection("labAppointments")
    .doc(data.appointment.id)
    .update({
      test: true,
      patients: { ...data.appointment.patients },
    });

  return syncAppointment(data.appointment);
}

function syncAppointment(appointment: any) {
  const appointmentData = { ...appointment };
  if (appointmentData.status === "completed") {
    return appointmentData;
  }
  const statusIncompleteUsers = Object.values(appointmentData.patients)?.filter(
    (u: any) => {
      const incompleteTest = Object.values(u.tests).filter(
        (t: any) => t.status !== "completed"
      );
      if (incompleteTest.length === 0) {
        u.status = "completed";
      }
      return incompleteTest.length > 0;
    }
  );
  if (statusIncompleteUsers.length === 0) {
    appointmentData.status = "completed";
  }
  return appointmentData;
}

export function useUpdateTest() {
  const queryCache = useQueryCache();
  return useMutation((data) => updateTest(data), {
    onSuccess: (appointment: any) => {
      queryCache.invalidateQueries("appointments");
      queryCache.setQueryData(["appointments", appointment.id], appointment);
    },
  });
}

async function updateTest(data: any) {
  if (Array.from(data.document).find((i: any) => i.size > 5 * 1024 * 1024)) {
    throw new Error("file too big");
  }
  const filesUploadPromises = Array.from(data.document).map((doc: any) => {
    return app
      .storage()
      .ref()
      .child("labAppointment")
      .child(data.appointment.id)
      .child(randomizeFileName(doc.name))
      .put(doc)
      .then((snap) =>
        snap.ref
          .getDownloadURL()
          .then((url) => ({ name: doc.name, url: url, createdAt: Date.now() }))
      );
  });

  const files = await Promise.all(filesUploadPromises);

  let updatedPatients = { ...data.appointment.patients };
  let updatedTest =
    updatedPatients[data.patient.profileId].tests[data.test.testId];
  updatedTest.status = data.status;
  updatedTest.remarks = data.remarks;
  updatedTest.files = [...(updatedTest.files || []), ...files];

  await app
    .firestore()
    .collection("users")
    .doc(data.appointment.userId)
    .collection("labAppointments")
    .doc(data.appointment.id)
    .update({
      patients: updatedPatients,
    });

  return syncAppointment(data.appointment);
}

export function useDeleteImageFromAppointment(fromTest = false) {
  const queryCache = useQueryCache();
  return useMutation(
    (data: any) =>
      fromTest ? deleteImageFromTest(data) : deleteImageFromPatient(data),
    {
      onSuccess: (_, variables) => {
        queryCache.setQueryData(
          ["appointments", variables.appointment.id],
          (old: any) => {
            if (!old) return variables.appointment;
            let updatedPatients = { ...old.patients };
            let patient = updatedPatients[variables.patient.profileId];
            if (fromTest) {
              patient.tests[variables.test.testId].files = patient.tests[
                variables.test.testId
              ].files.filter((i: any) => i.url !== variables.file.url);

              old.patients = updatedPatients;
            } else {
              patient.files = patient.files.filter(
                (i: any) => i.url !== variables.file.url
              );

              old.patients = updatedPatients;
            }
            return old;
          }
        );
        queryCache.setQueryData(["appointments"], (old: any) => {
          const updatedAppointment: any = queryCache.getQueryData([
            "appointments",
            variables.appointment.id,
          ]);
          return old.map((appointment: any) => {
            if (appointment.id === updatedAppointment.id) {
              return updatedAppointment;
            }
            return appointment;
          });
        });
      },
    }
  );
}

async function deleteImageFromTest(data: any) {
  const imageRef = app.storage().refFromURL(data.file.url);

  await imageRef.delete();

  let updatedPatients = { ...data.appointment.patients };
  let patient = updatedPatients[data.patient.profileId];
  patient.tests[data.test.testId].files = patient.tests[
    data.test.testId
  ].files.filter((i: any) => i.url !== data.file.url);

  return await app
    .firestore()
    .collection("users")
    .doc(data.appointment.userId)
    .collection("labAppointments")
    .doc(data.appointment.id)
    .update({
      patients: updatedPatients,
    });
}

async function deleteAllImagesFromTest({ test, appointment, patientId }: any) {
  await Promise.all(
    test.files.map((i: any) =>
      app
        .storage()
        .refFromURL(i.url)
        .delete()
        .catch((e) => console.error(e))
    )
  );

  let patients = { ...appointment.patients };

  patients[patientId].tests[test.testId].files = null;
  return await app
    .firestore()
    .collection("users")
    .doc(appointment.userId)
    .collection("labAppointments")
    .doc(appointment.id)
    .update({
      patients: patients,
    });
}

export function useDeleteAllFilesFromTest() {
  const queryCache = useQueryCache();
  return useMutation((data: any) => deleteAllImagesFromTest(data), {
    onSuccess: (_, variables) => {
      queryCache.invalidateQueries(["appointments"]);
    },
  });
}

async function deleteAllImagesFromPatient({ appointment, patient }: any) {
  await Promise.all(
    patient.files.map((i: any) =>
      app
        .storage()
        .refFromURL(i.url)
        .delete()
        .catch((e) => console.error(e))
    )
  );

  let patients = { ...appointment.patients };

  patients[patient.profileId].files = null;
  return await app
    .firestore()
    .collection("users")
    .doc(appointment.userId)
    .collection("labAppointments")
    .doc(appointment.id)
    .update({
      patients: patients,
    });
}

export function useDeleteAllFilesFromPatient() {
  const queryCache = useQueryCache();
  return useMutation((data: any) => deleteAllImagesFromPatient(data), {
    onSuccess: (_, variables) => {
      queryCache.invalidateQueries(["appointments"]);
    },
  });
}

async function deleteImageFromPatient(data: any) {
  const imageRef = app.storage().refFromURL(data.file.url);
  try {
    await imageRef.delete();
  } catch (e) { }

  let updatedPatients = { ...data.appointment.patients };
  let patient = updatedPatients[data.patient.profileId];
  patient.files = patient.files.filter((i: any) => i.url !== data.file.url);

  return await app
    .firestore()
    .collection("users")
    .doc(data.appointment.userId)
    .collection("labAppointments")
    .doc(data.appointment.id)
    .update({
      patients: updatedPatients,
    });
}

async function createTransaction(appointment: any) {
  let transactionData = {
    id: "",
    appointmentId: appointment.id,
    labId: appointment.labId,
    online: false,
    submitSampleAtLab: appointment.submitSampleAtLab,
    position: appointment.shiftPosition,
    shiftId: appointment.shiftId,
    status: "confirmed",
    createdAt: new Date().getTime(),
    lastUpdated: new Date().getTime(),
    scheduledCollectionTime: appointment.scheduledCollectionTime,
    collectingBoyId: null,
    userId: appointment.userId,
    amount: appointment.fee,
    paymentSettlement: appointment.paymentSettlement,
    from: appointment.user.name,
  };

  let userRef = app
    .firestore()
    .collection("users")
    .doc(appointment.userId)

  const userExists = (await userRef.get()).exists

  if (!userExists) {
    let transactionRef = app
      .firestore()
      .collection("temporaryUsers")
      .doc(appointment.userId)
      .collection("labTransactions")
      .doc();

    transactionData.id = transactionRef.id;
    await transactionRef.set(transactionData);
    return
  }

  let transactionRef = userRef
    .collection("labTransactions")
    .doc();
  transactionData.id = transactionRef.id;
  await transactionRef.set(transactionData);
}

export function useCreateTransaction() {
  const queryCache = useQueryCache();

  return useMutation((data) => createTransaction(data), {
    onSuccess: (_, appointment: any) => {
      queryCache.setQueryData(["appointments", appointment.id], (old: any) => {
        return {
          ...old,
          paymentStatus: "paid",
          status: "sample_collected",
        };
      });
    },
  });
}

async function markSampleCollected(appointment: any) {
  let userRef = app
    .firestore()
    .collection("users")
    .doc(appointment.userId)

  const userExists = (await userRef.get()).exists

  if (!userExists) {
    app
      .firestore()
      .collection("temporaryUsers")
      .doc(appointment.userId)
      .collection("labAppointments")
      .doc(appointment.id)
      .update({
        status: "sample_collected",
      });
    return;
  }

  await userRef
    .collection("labAppointments")
    .doc(appointment.id)
    .update({
      status: "sample_collected",
    });
  return;
}

export function useMarkSampleCollected() {
  const queryCache = useQueryCache();

  return useMutation((data) => markSampleCollected(data), {
    onSuccess: (_, appointment: any) => {
      queryCache.setQueryData(["appointments", appointment.id], (old: any) => {
        return {
          ...old,
          status: "sample_collected",
        };
      });
    },
  });
}
